// KokoroTTS.ts
'use client';
import type { ITTSEngine, TTSOptions, TTSCapabilities } from './types';
import * as Comlink from 'comlink';

// ============ KokoroWorkerAPI: Singleton Worker Setup ============
let worker: Worker | null = null;
let kokoroWorkerApi: Comlink.Remote<KokoroWorkerAPI> | null = null;

interface KokoroWorkerAPI {
  setCallbacks(callbacks: {
    onSentenceStart?: (text: string) => void;
    onReady?: (device: string) => void;
    onError?: (error: string) => void;
  }): void;
  initialize(): Promise<{ success: boolean; device: string; error?: string }>;
  initAudio(
    audioSAB: SharedArrayBuffer,
    pointerSAB: SharedArrayBuffer,
  ): Promise<void>;
  speak(text: string, isArticle?: boolean): Promise<void>;
  stop(): void;
  checkReady(): boolean;
  getDeviceInfo(): { device: string; isWebGPU: boolean };
}

function getKokoroWorker(): Promise<Comlink.Remote<KokoroWorkerAPI>> {
  return new Promise((resolve) => {
    if (worker && kokoroWorkerApi) return resolve(kokoroWorkerApi);

    worker = new Worker(new URL('./KokoroWebWorker.tsx', import.meta.url), {
      type: 'module',
    });

    const handleMessage = (e: MessageEvent) => {
      if (e.data === 'KOKORO_READY') {
        console.log('Main Thread: Worker signaled readiness.');
        kokoroWorkerApi = Comlink.wrap<KokoroWorkerAPI>(worker!);
        worker!.removeEventListener('message', handleMessage);
        resolve(kokoroWorkerApi);
      }
    };

    worker.addEventListener('message', handleMessage);
  });
}

// ============ KokoroWorkerAPI: AudioWorklet Setup ============
interface AudioState {
  context: AudioContext | null;
  workletNode: AudioWorkletNode | null;
  audioSAB: SharedArrayBuffer | null;
  pointerSAB: SharedArrayBuffer | null;
}

export class KokoroTTS implements ITTSEngine {
  private kokoroWorkerApi: Comlink.Remote<KokoroWorkerAPI> | null = null;
  private audio: AudioState = {
    context: null,
    workletNode: null,
    audioSAB: null,
    pointerSAB: null,
  };
  private isInitializing = false;
  private initPromise: Promise<void> | null = null;
  private currentCallbacks: Partial<TTSOptions> = {};
  private isReady = false;
  private device: string = 'none';

  constructor() {}

  GetEngineName(): string {
    return 'Kokoro';
  }

  isAvailable(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof Worker !== 'undefined' &&
      typeof AudioContext !== 'undefined' &&
      typeof SharedArrayBuffer !== 'undefined' &&
      !this.isIOS()
    );
  }

  async initialize(): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('KokoroTTS not available in this environment');
    }

    if (this.isReady) return;

    // Get shared worker instance for kokoro
    this.kokoroWorkerApi = await getKokoroWorker();

    // Initialize TTS (downloads model)
    let result;
    try {
      result = await this.kokoroWorkerApi.initialize();
    } catch (err: any) {
      throw new Error(err?.message || 'Failed to initialize Kokoro');
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to initialize Kokoro');
    }

    this.device = result.device;
    this.isReady = true;
  }

  /*
  This function is used to initialize the audio context and worklet.
  It is called when the user starts speaking.
  */
  async initAudio(): Promise<void> {
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    if (this.audio.context?.state === 'running') {
      return;
    }

    this.isInitializing = true;
    this.initPromise = this.doInitAudio();

    try {
      await this.initPromise;
    } finally {
      this.isInitializing = false;
      this.initPromise = null;
    }
  }

  /*
  This function is used to initialize the audio context and worklet.
  It is called when the user starts speaking.
  */
  private async doInitAudio(): Promise<void> {
    await this.cleanupAudio();

    try {
      // 1. Create AudioContext
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      this.audio.context = new AudioContextClass({ sampleRate: 24000 });

      // 2. Create SharedArrayBuffers
      this.audio.audioSAB = new SharedArrayBuffer(24000 * 5 * 4);
      this.audio.pointerSAB = new SharedArrayBuffer(8);

      // 3. Load AudioWorklet
      await this.loadAudioWorklet();

      // 4. Setup handlers
      this.setupAudioWorkletHandlers();

      // 5. Send INIT to AudioWorklet/audioSAB and pointerSAB
      // The audioSAB and pointerSAB is the channel between the audio worklet and the worker.
      this.audio.workletNode?.port.postMessage({
        type: 'INIT',
        audioSAB: this.audio.audioSAB,
        pointerSAB: this.audio.pointerSAB,
      });

      // 6. Send SAB to worker via Comlink
      await this.kokoroWorkerApi?.initAudio(
        this.audio.audioSAB,
        this.audio.pointerSAB,
      );

      // 7. Connect
      this.audio.workletNode?.connect(this.audio.context.destination);

      // 8. Resume
      if (this.audio.context.state === 'suspended') {
        await this.audio.context.resume();
      }
    } catch (error) {
      await this.cleanupAudio();
      throw error;
    }
  }

  /*
  This function is used to load the audio worklet.
  */
  private async loadAudioWorklet(): Promise<void> {
    if (!this.audio.context) throw new Error('AudioContext not created');

    const workletUrl = `/webworker/audio-processor.js`;

    try {
      await this.audio.context.audioWorklet.addModule(workletUrl);
    } catch (error) {
      throw new Error(`Failed to load AudioWorklet: ${error}`);
    }

    this.audio.workletNode = new AudioWorkletNode(
      this.audio.context,
      'kokoro-audio-processor',
      {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      },
    );
  }

  /*
  This function is used to set up the handlers for the audio worklet.
  It is called when the user starts speaking.
  */
  private setupAudioWorkletHandlers(): void {
    if (!this.audio.workletNode) return;

    this.audio.workletNode.port.onmessage = (e) => {
      switch (e.data.type) {
        case 'UNDERRUN':
          console.warn('Audio underrun detected');
          break;
        case 'ERROR':
          console.error('AudioWorklet error:', e.data.error);
          this.currentCallbacks.onError?.(new Error(e.data.error));
          break;
      }
    };

    this.audio.workletNode.onprocessorerror = (err) => {
      console.error('AudioWorklet processor error:', err);
      this.currentCallbacks.onError?.(new Error('Audio processing error'));
    };
  }

  /*
  This function is used to speak the text.
  */
  async speak(options: TTSOptions): Promise<void> {
    if (!this.isReady) {
      await this.initialize();
    }

    await this.initAudio();

    if (this.audio.context?.state === 'suspended') {
      await this.audio.context.resume();
    }

    this.currentCallbacks = options;

    return new Promise((resolve, reject) => {
      // Wrap callbacks
      const wrappedOptions = {
        ...options,
        onEnd: () => {
          options.onEnd?.();
          resolve();
        },
        onError: (err: Error) => {
          options.onError?.(err);
          reject(err);
        },
      };

      this.currentCallbacks = wrappedOptions;

      // Call worker via Comlink
      this.kokoroWorkerApi?.speak(options.text, options.isArticle ?? false);
    });
  }

  stop(): void {
    this.kokoroWorkerApi?.stop();
    this.audio.workletNode?.port.postMessage({ type: 'CLEAR' });
  }

  pause(): void {
    this.audio.context?.suspend();
  }

  resume(): void {
    this.audio.context?.resume();
  }

  getCapabilities(): TTSCapabilities {
    return {
      supportsStreaming: true,
      supportsOffline: true,
      maxTextLength: 5000,
    };
  }

  destroy(): void {
    this.cleanupAudio();
    // Don't terminate worker - it's shared!
  }

  private async cleanupAudio(): Promise<void> {
    try {
      this.audio.workletNode?.disconnect();
      this.audio.workletNode?.port.close();
    } catch {
      /* ignore */
    }

    try {
      await this.audio.context?.close();
    } catch {
      /* ignore */
    }

    this.audio = {
      context: null,
      workletNode: null,
      audioSAB: null,
      pointerSAB: null,
    };
  }

  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  getAudioState(): {
    contextState: string;
    hasWorklet: boolean;
    hasSAB: boolean;
  } {
    return {
      contextState: this.audio.context?.state || 'none',
      hasWorklet: !!this.audio.workletNode,
      hasSAB: !!this.audio.audioSAB,
    };
  }

  getDeviceInfo(): { device: string; isWebGPU: boolean } {
    return {
      device: this.device,
      isWebGPU: this.device === 'WebGPU',
    };
  }
}
